<template>
  <div class="register-page min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 bg-neutral-50">
    <div class="max-w-md w-full space-y-8 bg-white p-8 rounded-2xl shadow-sm border border-neutral-100">
      <div class="text-center">
        <h2 class="mt-6 text-3xl font-extrabold text-neutral-900">
          创建新账户
        </h2>
        <p class="mt-2 text-sm text-neutral-600">
          加入我们，探索更多AI工具
        </p>
      </div>

      <!-- 错误/成功提示 -->
      <div v-if="message.content" :class="['p-4 rounded-lg text-sm mb-4', message.type === 'error' ? 'bg-red-50 text-red-600' : 'bg-green-50 text-green-600']">
        <i :class="['fa mr-2', message.type === 'error' ? 'fa-exclamation-circle' : 'fa-check-circle']"></i>
        {{ message.content }}
      </div>

      <form class="mt-8 space-y-6" @submit.prevent="handleRegister">
        <div class="rounded-md shadow-sm space-y-4">
          <div>
            <label for="username" class="block text-sm font-medium text-neutral-700 mb-1">用户名</label>
            <input 
              id="username" 
              name="username" 
              type="text" 
              required 
              v-model="form.username"
              class="appearance-none relative block w-full px-3 py-3 border border-neutral-300 placeholder-neutral-500 text-neutral-900 rounded-lg focus:outline-none focus:ring-primary focus:border-primary focus:z-10 sm:text-sm" 
              placeholder="请输入用户名"
            >
          </div>
          
          <div>
            <label for="email" class="block text-sm font-medium text-neutral-700 mb-1">邮箱</label>
            <input 
              id="email" 
              name="email" 
              type="email" 
              required 
              v-model="form.email"
              class="appearance-none relative block w-full px-3 py-3 border border-neutral-300 placeholder-neutral-500 text-neutral-900 rounded-lg focus:outline-none focus:ring-primary focus:border-primary focus:z-10 sm:text-sm" 
              placeholder="请输入邮箱"
            >
          </div>

          <div>
            <label for="password" class="block text-sm font-medium text-neutral-700 mb-1">密码</label>
            <input 
              id="password" 
              name="password" 
              type="password" 
              required 
              v-model="form.password"
              @input="checkPasswordStrength"
              class="appearance-none relative block w-full px-3 py-3 border border-neutral-300 placeholder-neutral-500 text-neutral-900 rounded-lg focus:outline-none focus:ring-primary focus:border-primary focus:z-10 sm:text-sm" 
              placeholder="请输入密码"
            >
            <!-- 密码强度指示器 -->
            <div v-if="form.password" class="mt-2">
              <div class="flex h-1.5 w-full bg-neutral-200 rounded-full overflow-hidden">
                <div 
                  :class="['h-full transition-all duration-300', passwordStrengthColor]" 
                  :style="{ width: passwordStrengthWidth }"
                ></div>
              </div>
              <p class="text-xs mt-1 text-neutral-500 text-right">{{ passwordStrengthText }}</p>
            </div>
          </div>

          <div>
            <label for="confirm-password" class="block text-sm font-medium text-neutral-700 mb-1">确认密码</label>
            <input 
              id="confirm-password" 
              name="confirm-password" 
              type="password" 
              required 
              v-model="form.confirmPassword"
              class="appearance-none relative block w-full px-3 py-3 border border-neutral-300 placeholder-neutral-500 text-neutral-900 rounded-lg focus:outline-none focus:ring-primary focus:border-primary focus:z-10 sm:text-sm" 
              placeholder="请再次输入密码"
            >
          </div>
        </div>

        <div class="flex items-center">
          <input 
            id="agree-terms" 
            name="agree-terms" 
            type="checkbox" 
            required
            v-model="form.agreeTerms"
            class="h-4 w-4 text-primary focus:ring-primary border-neutral-300 rounded cursor-pointer"
          >
          <label for="agree-terms" class="ml-2 block text-sm text-neutral-900 cursor-pointer">
            我已阅读并同意
            <a href="#" class="font-medium text-primary hover:text-primary-dark">服务条款</a>
            和
            <a href="#" class="font-medium text-primary hover:text-primary-dark">隐私政策</a>
          </label>
        </div>

        <div>
          <button 
            type="submit" 
            :disabled="isLoading"
            class="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors disabled:opacity-70"
          >
            <span class="absolute left-0 inset-y-0 flex items-center pl-3">
              <i class="fa fa-user-plus group-hover:text-primary-light" aria-hidden="true"></i>
            </span>
            {{ isLoading ? '注册中...' : '注册' }}
          </button>
        </div>
      </form>
      
      <div class="text-center mt-4">
        <p class="text-sm text-neutral-600">
          已有账户? 
          <router-link to="/login" class="font-medium text-primary hover:text-primary-dark">
            立即登录
          </router-link>
        </p>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'RegisterPage',
  data() {
    return {
      form: {
        username: '',
        email: '',
        password: '',
        confirmPassword: '',
        agreeTerms: false
      },
      message: {
        type: '',
        content: ''
      },
      isLoading: false,
      passwordStrength: 0
    }
  },
  computed: {
    passwordStrengthWidth() {
      return (this.passwordStrength / 4) * 100 + '%';
    },
    passwordStrengthColor() {
      if (this.passwordStrength <= 1) return 'bg-red-500';
      if (this.passwordStrength === 2) return 'bg-yellow-500';
      if (this.passwordStrength === 3) return 'bg-blue-500';
      return 'bg-green-500';
    },
    passwordStrengthText() {
      if (this.passwordStrength === 0) return '';
      if (this.passwordStrength <= 1) return '弱';
      if (this.passwordStrength === 2) return '中';
      if (this.passwordStrength === 3) return '强';
      return '极强';
    }
  },
  methods: {
    checkPasswordStrength() {
      let strength = 0;
      const pwd = this.form.password;
      if (pwd.length >= 6) strength++;
      if (pwd.match(/[a-z]/) && pwd.match(/[A-Z]/)) strength++;
      if (pwd.match(/\d/)) strength++;
      if (pwd.match(/[^a-zA-Z\d]/)) strength++;
      this.passwordStrength = strength;
    },
    handleRegister() {
      this.message = { type: '', content: '' };
      
      if (this.form.password !== this.form.confirmPassword) {
        this.message = { type: 'error', content: '两次输入的密码不一致' };
        return;
      }
      
      this.isLoading = true;
      
      // 模拟注册请求
      setTimeout(() => {
        this.isLoading = false;
        
        // 注册成功
        this.message = { type: 'success', content: '注册成功！正在跳转登录...' };
        
        // 跳转登录页
        setTimeout(() => {
          this.$router.push('/login');
        }, 1500);
      }, 1500);
    }
  }
}
</script>
